// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Which language this browser is reading in (01 §6, 08 §3).
//
// WHY THIS IS ITS OWN MODULE, AND A SMALL ONE
// -------------------------------------------
// **The front door needs it and the front door may reach nothing.** FR-030 keeps the page a visitor
// plays NEON NOVA on free of anything that could construct a Supabase client, and that page has a
// paywall entry, an error toast and a settings link on it — all of which have words. So the
// language decision has to live somewhere with no dependencies at all, exactly like `gateVerdict`
// and for exactly the same reason.
//
// **Two languages, closed.** English and French, and the type says so. A `string` here would be a
// bundle lookup that can miss, which is a screen with a key on it where a sentence belongs.
//
// WHY fr-CA AND NOT fr
// --------------------
// The pairing that arrived with it — French *and* Canadian dollars — is what settles this. This is
// a Canadian market, so the French is Québec French and the locale that formats its money and its
// dates is `fr-CA`. It is not a cosmetic choice: `fr-FR` renders C$121.00 as `121,00 $CA` with a
// non-breaking space in a different place, writes dates the other way round, and is the wrong
// register for a Canadian reader. English gets `en-CA` for the same reason, which is what puts the
// dollar sign in front and the day before the month.
//
// **The locale is derived, never stored.** One fact — the language — and everything else read off
// it. Storing a locale beside a language is storing the same decision twice.

import { readLocal, safeStorage, writeLocal, type StorageLike } from '../../storage/local'
import { stampPreference } from '../../storage/prefStamps'

export type Language = 'en' | 'fr'

export const LANGUAGES: readonly Language[] = ['en', 'fr']

/** Where the choice is kept. One key, so it cannot be spelled two ways. */
export const LANGUAGE_KEY = 'hcc.pinball.language'

export function isLanguage(value: unknown): value is Language {
  return typeof value === 'string' && (LANGUAGES as readonly string[]).includes(value)
}

/**
 * The BCP 47 locale each language formats with.
 *
 * Used for money, dates and `<html lang>`. See the header for why both are Canadian.
 */
export const LOCALES: Readonly<Record<Language, string>> = {
  en: 'en-CA',
  fr: 'fr-CA',
}

export function localeOf(language: Language): string {
  return LOCALES[language]
}

/**
 * What the browser is asking for, before anybody has chosen.
 *
 * **Any French at all means French.** `fr`, `fr-CA`, `fr-FR` and a `navigator.languages` list whose
 * second entry is French all mean a reader who would rather read French than English, and matching
 * only the exact tag would hand a Québécois reader an English page because their phone says
 * `fr-CA` and this product listed `fr`. Everything else — including no answer at all — is English,
 * because English is what this product is written in and a wrong guess in that direction is
 * survivable in a way the other is not.
 */
export function browserLanguage(
  navigatorLike: { languages?: readonly string[]; language?: string } | undefined =
    (globalThis as { navigator?: { languages?: readonly string[]; language?: string } }).navigator,
): Language {
  const tags = navigatorLike?.languages ?? (navigatorLike?.language === undefined ? [] : [navigatorLike.language])
  for (const tag of tags) {
    if (typeof tag === 'string' && tag.toLowerCase().startsWith('fr')) return 'fr'
  }
  return 'en'
}

/**
 * The language to render in: what the player chose, or what their browser asked for.
 *
 * A stored choice **outranks the browser** and keeps doing so after the browser changes, because it
 * is a statement about a person rather than about a device — the same distinction `TierChoice`
 * draws between an override and a measurement.
 */
export function readLanguage(storage: StorageLike | undefined = safeStorage()): Language {
  const stored = readLocal(storage, LANGUAGE_KEY)
  return isLanguage(stored) ? stored : browserLanguage()
}

export function writeLanguage(
  language: Language,
  storage: StorageLike | undefined = safeStorage(),
): void {
  writeLocal(storage, LANGUAGE_KEY, language)
  stampPreference('language', storage)
}

/**
 * Tell the document what language it is in.
 *
 * **`<html lang>` is not decoration.** It is what a screen reader picks a voice from, what a
 * browser offers to translate against, and what hyphenation and quotation marks are chosen by. A
 * French page served as `lang="en"` is read aloud in an English accent, which is worse than not
 * translating it at all.
 */
export function applyDocumentLanguage(language: Language): void {
  const doc = (globalThis as { document?: { documentElement?: { lang: string } } }).document
  if (doc?.documentElement === undefined) return
  doc.documentElement.lang = localeOf(language)
}
